import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'

interface MergeContactsData {
  ghostProfileId: string
  realContactAccountId: string
}

interface MergeContactsResponse {
  success: boolean
  // Id of the private note where the server kept what was typed on the merged
  // card. Absent on a server that does not keep it, null when the card was empty.
  detailsNoteId?: string | null
}

const mergeContacts = async (data: MergeContactsData): Promise<MergeContactsResponse> => {
  const { data: response } = await api.post<MergeContactsResponse>('/ft_merge-contacts', data)
  return response
}

interface MergeContactsCallbacks {
  onSuccess?: (response: MergeContactsResponse, variables: MergeContactsData) => void
  onError?: (error: Error, variables: MergeContactsData) => void
}

// The callbacks sit on the mutation itself, not on each mutate() call: those
// only fire for the latest call and never once the screen is closed, so a
// merge that failed behind another one would say nothing.
export const useFtMergeContacts = (callbacks: MergeContactsCallbacks = {}) => {
  const queryClient = useQueryClient()

  return useMutation<MergeContactsResponse, Error, MergeContactsData>({
    mutationFn: mergeContacts,
    onSuccess: (response, variables) => callbacks.onSuccess?.(response, variables),
    onError: (error, variables) => callbacks.onError?.(error, variables),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ft-merge-suggestions'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['suggested-contacts'] }),
        // A merge moves the card's notes, may create one more, and removes the
        // card from the user's appointments.
        queryClient.invalidateQueries({ queryKey: ['notes'] }),
        queryClient.invalidateQueries({ queryKey: ['notes-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['calendar-events'] }),
      ])
    },
  })
}
